import {
  Avatar as MuiAvatar,
  AvatarProps as MuiAvatarProps,
  SvgIconTypeMap,
  useTheme,
} from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { FC, SyntheticEvent, useState } from 'react';
import { getContrastTextColor } from '../../utils/get-contrast-text-color';
import { getImageAverageBrightness } from '../../utils/get-image-average-brightness';
import { hashColorKey } from '../../utils/hash-color-key';

export type AvatarProps = MuiAvatarProps & {
  text?: string;
  colorKey?: Record<string, string | number>;
  size?: 'md' | 'lg' | 'xl';
  color?: 'random' | 'grey';
  icon?: OverridableComponent<SvgIconTypeMap<object>>;
};

const Avatar: FC<AvatarProps> = ({
  text,
  colorKey,
  color,
  size = 'md',
  icon: Icon,
  src,
  ...rest
}) => {
  const theme = useTheme();

  const bgColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.success.main,
  ];

  const sizes = {
    md: 24,
    lg: 32,
    xl: 40,
  };

  const fontSizes = {
    md: theme.fontSizes.sm,
    lg: undefined,
    xl: undefined,
  };

  const iconSizes = {
    md: 14,
    lg: undefined,
    xl: undefined,
  };

  const [imageBgColor, setImageBgColor] = useState<string | undefined>(
    undefined,
  );

  const handleImageLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    try {
      const avgBrightness = getImageAverageBrightness(e.currentTarget);
      setImageBgColor(avgBrightness > 200 ? '#000000' : '#ffffff');
    } catch {
      setImageBgColor(undefined);
    }
  };

  const bgColor = src
    ? imageBgColor
    : colorKey
    ? hashColorKey(colorKey)
    : color === 'random'
    ? bgColors[text?.length ? text.charCodeAt(0) % 6 : 1]
    : undefined;
  const iconColor = Icon && bgColor ? getContrastTextColor(bgColor) : undefined;

  return (
    <MuiAvatar
      sx={{
        backgroundColor: bgColor,
        width: sizes[size],
        height: sizes[size],
        fontSize: fontSizes[size],
      }}
      src={src}
      slotProps={{ img: { onLoad: handleImageLoad, crossOrigin: 'anonymous' } }}
      {...rest}
    >
      {text?.length ? (
        text.charAt(0).toUpperCase()
      ) : Icon ? (
        <Icon sx={{ color: iconColor, fontSize: iconSizes[size] }} />
      ) : null}
    </MuiAvatar>
  );
};

export default Avatar;
